import { alpha, type Theme } from '@mui/material/styles';

/** Motion for the assistant while it works, so waiting feels like progress. All of it stops under reduced motion. */

const still = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } as const;

/** A new step or a finished answer's extras rise into place. */
export const riseSx = {
  '@keyframes rise': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'rise 260ms ease-out both',
  ...still,
} as const;

/** Each streamed sentence fades in rather than popping. */
export const fadeInSx = {
  '@keyframes fadeIn': { from: { opacity: 0, filter: 'blur(2px)' }, to: { opacity: 1, filter: 'none' } },
  animation: 'fadeIn 420ms ease-out both',
  ...still,
} as const;

/** A light sweep across the label of the step in progress. */
export const shimmerTextSx = (theme: Theme) => ({
  '@keyframes shimmer': { from: { backgroundPosition: '100% 0' }, to: { backgroundPosition: '-100% 0' } },
  // The band only lightens the text, so every letter stays readable as it passes.
  backgroundImage: `linear-gradient(90deg, currentColor 30%, ${alpha(theme.palette.text.secondary, 0.35)} 50%, currentColor 70%)`,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'shimmer 1.6s linear infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', WebkitTextFillColor: 'currentColor' },
});

/** A soft ring around the assistant's avatar while it is working. */
export const pulseRingSx = (theme: Theme) => ({
  '@keyframes pulseRing': {
    '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.secondary.main, 0.35)}` },
    '70%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.secondary.main, 0)}` },
    '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.secondary.main, 0)}` },
  },
  animation: 'pulseRing 1.6s ease-out infinite',
  ...still,
});

/** One of the three typing dots; the caller staggers them with animationDelay. */
export const dotSx = {
  '@keyframes dot': {
    '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
    '40%': { opacity: 1, transform: 'translateY(-3px)' },
  },
  display: 'inline-block',
  width: 5,
  height: 5,
  mx: '2px',
  borderRadius: '50%',
  bgcolor: 'text.secondary',
  animation: 'dot 1.2s ease-in-out infinite',
  ...still,
} as const;

/** The step in progress turns, like a refresh. */
export const spinSx = {
  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
  animation: 'spin 1.1s linear infinite',
  ...still,
} as const;
